import React from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import { scaleOP } from '@components/frame';
import { fontOP } from '@styles/fonts';
import { formatDateToLocalDateTime } from '@helpers';
import TouchableElement from '@components/common/touchableElement';
import LinkIcon from '@v2/context/ProductionBatch/linkIcon';
import { NC_FILE_STATUS } from '@v2/context/ProductionBatch/linkIcon/helpers/resolveLinkIcon';
import ImageContext from '@v2/context/Image/context';

type LinkIconNCFileStatus = keyof typeof NC_FILE_STATUS;
import { DOCUMENT_ENTITY_TYPE, IMAGE_HANDLING_CONTEXT } from '@components/imageHandling/common';
import { TILE_CONFIG, TILE_ASPECT_RATIO } from '@v2/pages/common/constants/base.constants';
import { OperatorPanelBatchTileProps } from './types';
import StatusBadge from '@components/common/StatusBadge';
import { getStatusText } from '@components/search/ncFiles/helpers';

const OperatorPanelBatchTile = ({
  item,
  displayMode,
  itemSize,
  effectiveWidth,
  onPress,
  C,
}: OperatorPanelBatchTileProps) => {
  if (!item) return null;

  const isLineMode = displayMode === 'line';
  const isSmallMode = displayMode === 'square';
  const isInfoTilesMode = displayMode === 'infoTiles';

  const infoTileImgWidth = scaleOP(153); // From Figma: 153x102 (3:2 ratio)
  const imgSize = isLineMode
    ? { width: scaleOP(TILE_CONFIG.LINE_MODE_WIDTH), height: scaleOP(TILE_CONFIG.LINE_MODE_HEIGHT) }
    : isInfoTilesMode
    ? { width: infoTileImgWidth, height: infoTileImgWidth * TILE_ASPECT_RATIO }
    : { width: itemSize - scaleOP(2), height: (itemSize - scaleOP(2)) * TILE_ASPECT_RATIO };

  // Line mode rendering
  if (isLineMode) {
    return (
      <TouchableElement onPress={() => onPress(item)}>
        {({ pressHoverColor, boxStyle }) => (
          <View
            style={[
              boxStyle,
              {
                width: effectiveWidth,
                alignItems: 'center',
                marginBottom: scaleOP(TILE_CONFIG.SPACE_BETWEEN_NORMAL),
                flexDirection: 'row',
                backgroundColor: C(pressHoverColor('Background/Light/System')),
                padding: scaleOP(8),
              },
            ]}
          >
            <View style={{ marginRight: scaleOP(10), maxWidth: '100%' }}>
              <ImageContext
                image={{
                  documentId: item.imageDocumentId,
                  imageHash: item.imageHash,
                  contextId: item.productionBatchId,
                  imageData: item.imageData ? {
                    isCropped: item.imageData.isCropped,
                    extensionDerivative: item.imageData.extensionDerivative,
                    pageNumber: item.imageData.pageNumber,
                    rotationAngle: item.imageData.rotationAngle || 0,
                    rotationAngleCrop: item.imageData.rotationAngle || 0,
                  } : undefined,
                  width: imgSize.width,
                  height: imgSize.height,
                  borderRadius: scaleOP(4),
                  isProcessing: false,
                  contextCode: IMAGE_HANDLING_CONTEXT[DOCUMENT_ENTITY_TYPE.Batch],
                } as any}
                showImageControl={false}
              />
            </View>

            <View style={{ flex: 1, flexDirection: 'row', overflow: 'hidden', alignItems: 'center' }}>
              <Text style={[fontOP['Button 1'], { color: C('Label/Light/Primary'), flexShrink: 0 }]}>
                {item.batchNumber}
              </Text>
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                style={[
                  fontOP['Subhead 1'],
                  {
                    color: C('Label/Light/Tertiary'),
                    flexShrink: 1,
                    overflow: 'hidden',
                  },
                ]}
              >
                {' '}– {item.ncFileName}
              </Text>
            </View>

            <StatusBadge
              C={C}
              isClosed={item.isClosed}
              isInProgress={item.isInProgress}
              scale={scaleOP}
              font={fontOP}
            />
          </View>
        )}
      </TouchableElement>
    );
  }

  // Info Tiles mode rendering (horizontal card with full details)
  if (isInfoTilesMode) {
    return (
      <TouchableElement onPress={() => onPress(item)}>
        {({ pressHoverColor, boxStyle }) => (
          <View
            style={[
              boxStyle,
              {
                width: (effectiveWidth ?? 0) - scaleOP(36), // Account for container padding (18px * 2)
                flexDirection: 'row',
                backgroundColor: C(pressHoverColor('Background/Light/Field')),
                borderRadius: scaleOP(TILE_CONFIG.BORDER_RADIUS),
                padding: scaleOP(10),
                marginBottom: scaleOP(TILE_CONFIG.SPACE_BETWEEN_NORMAL),
              },
            ]}
          >
            {/* Square image on left */}
            <View
              style={{
                width: imgSize.width,
                height: imgSize.height,
                backgroundColor: C('Background/Light/System'),
                borderRadius: scaleOP(TILE_CONFIG.BORDER_RADIUS),
                overflow: 'hidden',
              }}
            >
              <ImageContext
                image={{
                  documentId: item.imageDocumentId,
                  imageHash: item.imageHash,
                  contextId: item.productionBatchId,
                  imageData: item.imageData ? {
                    isCropped: item.imageData.isCropped,
                    extensionDerivative: item.imageData.extensionDerivative,
                    pageNumber: item.imageData.pageNumber,
                    rotationAngle: item.imageData.rotationAngle || 0,
                    rotationAngleCrop: item.imageData.rotationAngle || 0,
                  } : undefined,
                  width: imgSize.width,
                  height: imgSize.height,
                  borderRadius: 0,
                  isProcessing: false,
                  contextCode: IMAGE_HANDLING_CONTEXT[DOCUMENT_ENTITY_TYPE.Batch],
                } as any}
                showImageControl={false}
              />
            </View>

            {/* Content on right */}
            <View style={{ flex: 1, marginLeft: scaleOP(12), gap: scaleOP(4) }}>
              {/* Row 1: NC file name + badge */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={[fontOP.Headline, { color: C('Label/Light/Primary'), flex: 1, marginRight: scaleOP(8) }]} numberOfLines={1}>
                  {item.ncFileName}
                </Text>
                <StatusBadge
                  C={C}
                  isClosed={item.isClosed}
                  isInProgress={item.isInProgress}
                  scale={scaleOP}
                  font={fontOP}
                />
              </View>

              {/* Row 2: Link icon + link details + quantity */}
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: scaleOP(6) }}>
                <LinkIcon
                  isWithERP={false}
                  linkStatus={item.linkStatus}
                  ncFileStatus={item.ncFileStatus as LinkIconNCFileStatus}
                  width={scaleOP(16)}
                  height={scaleOP(16)}
                />
                <Text style={[fontOP['Subhead 1'], { color: C('Label/Light/Tertiary'), flexShrink: 1 }]} numberOfLines={1}>
                  {item.links?.length > 1
                    ? `${i18n.t('Linked to')} ${item.links.length} ${i18n.t('jobs')}`
                    : item.links?.length === 1
                    ? `${i18n.t('Linked to')} ${item.links[0]?.productionOrderNumber || ''}`
                    : getStatusText(item.ncFileStatus) ?? i18n.t('Not linked to job')}
                </Text>
                <Text style={[fontOP['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>•</Text>
                <View style={{ flexDirection: 'row', alignItems: 'baseline', flexShrink: 0 }}>
                  <Text style={[fontOP['Time 2'], { color: C('Label/Light/Primary') }]}>
                    {item.quantity}{Number(item.links?.[0]?.quantityTarget) > 0 ? ' / ' : ''}
                  </Text>
                  {Number(item.links?.[0]?.quantityTarget) > 0 && (
                    <Text style={[fontOP['Time 4'], { color: C('Label/Light/Tertiary') }]}>
                      {item.links[0].quantityTarget}
                    </Text>
                  )}
                  <Text style={[fontOP['Caption 2'], { color: C('Label/Light/Tertiary'), marginLeft: scaleOP(2) }]}>
                    {i18n.t('Pcs')}
                  </Text>
                </View>
              </View>

              {/* Row 3: Part info (only for linked batches) */}
              {item.links?.[0] && (
                <>
                  {item.links[0].partName && (
                    <Text style={[fontOP['Subhead 1'], { color: C('Label/Light/Tertiary') }]} numberOfLines={1}>
                      {item.links[0].partName}
                    </Text>
                  )}
                  {(item.links[0].drawingNumber || item.links[0].drawingRevision) && (
                    <Text style={[fontOP['Subhead 1'], { color: C('Label/Light/Tertiary') }]} numberOfLines={1}>
                      {[item.links[0].drawingNumber, item.links[0].drawingRevision, item.links[0].drawingPosition].filter(Boolean).join(' • ')}
                    </Text>
                  )}
                </>
              )}

              {/* Row 4: Date range + machine */}
              <Text style={[fontOP['Subhead 1'], { color: C('Label/Light/Tertiary') }]} numberOfLines={1}>
                {formatDateToLocalDateTime(item.startedOn, false, false)}
                {item.isClosed && item.endedOn ? ` – ${formatDateToLocalDateTime(item.endedOn, false, false)}` : ''}
                {' • '}{item.machineShortName}
              </Text>
            </View>
          </View>
        )}
      </TouchableElement>
    );
  }

  // Block/Square mode rendering
  return (
    <TouchableElement onPress={() => onPress(item)}>
      {({ pressHoverColor, boxStyle }) => (
        <View
          style={[
            boxStyle,
            {
              width: itemSize,
              backgroundColor: C(pressHoverColor('Background/Light/Field')),
              borderRadius: scaleOP(TILE_CONFIG.BORDER_RADIUS),
              marginBottom: scaleOP(isSmallMode ? TILE_CONFIG.MARGIN_BOTTOM_SMALL : TILE_CONFIG.MARGIN_BOTTOM_NORMAL),
            },
          ]}
        >
          {/* Image */}
          <View
            style={{
              width: imgSize.width,
              height: imgSize.height,
              backgroundColor: C('Background/Light/System'),
              borderRadius: scaleOP(TILE_CONFIG.BORDER_RADIUS),
              overflow: 'hidden',
            }}
          >
            <ImageContext
              image={{
                documentId: item.imageDocumentId,
                imageHash: item.imageHash,
                contextId: item.productionBatchId,
                imageData: item.imageData ? {
                  isCropped: item.imageData.isCropped,
                  extensionDerivative: item.imageData.extensionDerivative,
                  pageNumber: item.imageData.pageNumber,
                  rotationAngle: item.imageData.rotationAngle || 0,
                  rotationAngleCrop: item.imageData.rotationAngle || 0,
                } : undefined,
                width: imgSize.width,
                height: imgSize.height,
                borderRadius: 0,
                isProcessing: false,
                contextCode: IMAGE_HANDLING_CONTEXT[DOCUMENT_ENTITY_TYPE.Batch],
              } as any}
              showImageControl={false}
            />
          </View>

          {/* Content */}
          {!isSmallMode && (
            <View style={{ paddingHorizontal: scaleOP(9), paddingTop: scaleOP(8), paddingBottom: scaleOP(10), gap: scaleOP(4) }}>
              {/* Row 1: NC file name + badge */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={[fontOP.Headline, { color: C('Label/Light/Primary'), flex: 1, marginRight: scaleOP(8) }]} numberOfLines={1}>
                  {item.ncFileName}
                </Text>
                <StatusBadge
                  C={C}
                  isClosed={item.isClosed}
                  isInProgress={item.isInProgress}
                  scale={scaleOP}
                  font={fontOP}
                />
              </View>

              {/* Row 2: Link icon + link details + quantity */}
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: scaleOP(6) }}>
                <LinkIcon
                  isWithERP={false}
                  linkStatus={item.linkStatus as 'NotLinked' | 'SingleLink' | 'MultiLink'}
                  ncFileStatus={item.ncFileStatus as LinkIconNCFileStatus}
                  width={scaleOP(16)}
                  height={scaleOP(16)}
                />
                <Text style={[fontOP['Subhead 1'], { color: C('Label/Light/Tertiary'), flexShrink: 1 }]} numberOfLines={1}>
                  {item.links?.length > 1
                    ? `${i18n.t('Linked to')} ${item.links.length} ${i18n.t('jobs')}`
                    : item.links?.length === 1
                    ? `${i18n.t('Linked to')} ${item.links[0]?.productionOrderNumber || ''}`
                    : getStatusText(item.ncFileStatus) ?? i18n.t('Not linked to job')}
                </Text>
                <Text style={[fontOP['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>•</Text>
                <View style={{ flexDirection: 'row', alignItems: 'baseline', flexShrink: 0 }}>
                  <Text style={[fontOP['Time 2'], { color: C('Label/Light/Primary') }]}>
                    {item.quantity}{Number(item.links?.[0]?.quantityTarget) > 0 ? ' / ' : ''}
                  </Text>
                  {Number(item.links?.[0]?.quantityTarget) > 0 && (
                    <Text style={[fontOP['Time 4'], { color: C('Label/Light/Tertiary') }]}>
                      {item.links[0].quantityTarget}
                    </Text>
                  )}
                  <Text style={[fontOP['Caption 2'], { color: C('Label/Light/Tertiary'), marginLeft: scaleOP(2) }]}>
                    {i18n.t('Pcs')}
                  </Text>
                </View>
              </View>

              {/* Row 3-4: Part info (only for linked batches) */}
              {item.links?.[0] && (
                <>
                  {item.links[0].partName && (
                    <Text style={[fontOP['Subhead 1'], { color: C('Label/Light/Tertiary') }]} numberOfLines={1}>
                      {item.links[0].partName}
                    </Text>
                  )}
                  {(item.links[0].drawingNumber || item.links[0].drawingRevision) && (
                    <Text style={[fontOP['Subhead 1'], { color: C('Label/Light/Tertiary') }]} numberOfLines={1}>
                      {[item.links[0].drawingNumber, item.links[0].drawingRevision, item.links[0].drawingPosition].filter(Boolean).join(' • ')}
                    </Text>
                  )}
                </>
              )}

              {/* Row 5: Date range + machine */}
              <Text style={[fontOP['Subhead 1'], { color: C('Label/Light/Tertiary') }]} numberOfLines={1}>
                {formatDateToLocalDateTime(item.startedOn, false, false)}
                {item.isClosed && item.endedOn ? ` – ${formatDateToLocalDateTime(item.endedOn, false, false)}` : ''}
                {' • '}{item.machineShortName}
              </Text>
            </View>
          )}
        </View>
      )}
    </TouchableElement>
  );
};

export default OperatorPanelBatchTile;
